import React, { useState, useEffect } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import config from "../config";
import Helper from "../helpers";
const baseUrl = config.apiBaseUrl;

// Where to go after logging in when the page was opened with ?returnUrl=/some/path (for example an
// invitation link). Only same site paths are allowed, so a crafted link cannot send people elsewhere.
const getReturnUrl = () => {
  const target = new URLSearchParams(window.location.search).get('returnUrl');
  if (target && target.startsWith('/') && !target.startsWith('//') && !target.includes('\\')) {
    return target;
  }
  return null;
};

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if the user is already logged in and if the token is not expired
    const checkLoginStatus = async () => {
      const token = await Helper.getCookie('wh-jwt');
      if (token) {
        const tokenData = Helper.parseJwt(token);
        const currentTime = Date.now() / 1000;

        if (tokenData.exp > currentTime) {
          navigate(getReturnUrl() || '/dashboard');
        } else {
          // Token has expired, delete it
          await Helper.deleteCookie('wh-jwt');
          await Helper.deleteCookie('wh.user.email');
          await Helper.deleteCookie('wh.user.id');
        }
      }
    };

    checkLoginStatus();
  }, [navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const formData = {
      Email: username,
      Password: password
    };

    fetch(`${baseUrl}/api/users/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('Failed to log in');
        }
      })
      .then(data => {
        var tokenData = Helper.parseJwt(data.token);

        Helper.setCookie('wh.user.email', tokenData.email, 5);
        Helper.setCookie('wh.user.id', tokenData.sub,5);

        if (tokenData.venues) {
          tokenData.venues.forEach(venue => {
            Helper.setCookie(`wh.venue.${venue.VenueShortCode}`, venue.VenueShortCode,5);
          });
        }

        if (tokenData.admin_venues) {
          tokenData.admin_venues.forEach(adminVenue => {
            Helper.setCookie(`wh.admin_venue.${adminVenue.VenueShortCode}`, adminVenue.VenueShortCode,5);
          });
        }
        setIsLoading(false);
        Helper.setCookie('wh-jwt',data.token,5);
        
        const isPtDirectoryOnly = tokenData.pt_directory === 'true' && !tokenData.admin_venue;
        navigate(getReturnUrl() || (isPtDirectoryOnly ? '/pt-directory' : '/dashboard'));
      })
      .catch((error) => {
        console.error('Error:', error);
        setIsLoading(false);
        setError('Invalid username or password. Please try again.');
      });
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-2">Login</h2>
          <p className="text-gray-400">Sign in to your account</p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-gray-900 border border-gray-700 text-red-300 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="input-field"
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-field"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary flex justify-center items-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </div>

          <div className="text-center">
            <Link 
              to="/forgotten-password"
              className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
